import React, {
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { CircularProgress } from '@mui/material';

import useStorage from '../../../../hooks/useStorage';
import useComponentMounted from '../../../../hooks/useComponentMounted';
import { AttachmentType, attachmentShape } from '../../../../utils/attachments';
import AttachmentError from '../AttachmentError';
import { fetchAsSafeImageUrl } from '../../../../../utils/dataProcessing';

const GenericAttachment = ({
  attachment,
  AttachmentComponent,
}) => {
  const {
    getDownloadUrlFromStoragePath,
  } = useStorage();
  const [attachmentDownloadUrl, setAttachmentDownloadUrl] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [showError, setShowError] = useState(false);
  const blobUrlRef = useRef(null);

  const isComponentMountedRef = useComponentMounted();

  useEffect(() => {
    const getAttachmentLocalUrl = async () => {
      try {
        const downloadUrl = await getDownloadUrlFromStoragePath(attachment.assetPath);

        let finalUrl = downloadUrl;
        if (attachment.type === AttachmentType.IMAGE) {
          const { safeUrl, isBlob } = await fetchAsSafeImageUrl(downloadUrl);
          finalUrl = safeUrl;
          if (isBlob) blobUrlRef.current = finalUrl;
        }

        if (isComponentMountedRef.current) {
          setAttachmentDownloadUrl(finalUrl);
        }
      } catch (error) {
        if (isComponentMountedRef.current) {
          setShowError(true);
        }
      } finally {
        if (isComponentMountedRef.current) {
          setIsLoading(false);
        }
      }
    };
    getAttachmentLocalUrl();
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, [
    attachment,
    getDownloadUrlFromStoragePath,
    isComponentMountedRef,
  ]);

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
          padding: '16px 0',
        }}
      >
        <CircularProgress name="crescent" size={28} style={{ color: '#000000' }} />
      </div>
    );
  }

  return (
    showError
      ? <AttachmentError />
      : <AttachmentComponent attachmentData={attachment} assetLocalUrl={attachmentDownloadUrl} />
  );
};

GenericAttachment.propTypes = {
  attachment: attachmentShape,
  AttachmentComponent: PropTypes.any.isRequired,
};

GenericAttachment.defaultProps = {
  attachment: {},
};

export default GenericAttachment;
